import { Fragment, useState } from 'react';
import { FileUp } from 'lucide-react';
import { toast } from 'sonner';

import { DialogTitle, DialogDescription, DialogHeader } from '@/components/ui/dialog';
import { useAddDeposit } from 'src/context/deposit';
import { Separator } from '@/components/ui/separator';
import { cn } from 'src/lib/utils';
import { SecureUploader, type UploadCompletionResult } from 'src/widgets/AssetUpload';
import useSecureUpload from 'src/hooks/use-secure-upload';

const SecureUploadContainer = () => {
  const [isUploading, setIsUploading] = useState(false);
  const {
    ui: { selectedEscrowId, setInitialData, getBack },
    refetch,
    contentType,
  } = useAddDeposit();

  const [uploadFiles, cancelUpload, , , uploadProgress] = useSecureUpload(selectedEscrowId);

  const handleReset = () => {
    setInitialData();
    refetch?.();
  };

  const handleUploadComplete = (result: UploadCompletionResult) => {
    if (result.status === 'success') {
      toast.success('Assets added!', { description: 'Your assets have been added to this escrow.' });
    } else if (result.status === 'canceled') {
      toast.error('Upload canceled');
    } else {
      toast.error(result.message);
    }
  };

  return (
    <Fragment>
      <DialogHeader className={cn('gap-1.5 relative', !isUploading && 'pl-13')}>
        {!isUploading && <FileUp className="size-9 absolute left-0 top-[50%] -translate-y-1/2" />}
        <DialogTitle className="text-xl">{isUploading ? 'Deposit in progress' : 'File upload'}</DialogTitle>
        <DialogDescription>
          {isUploading
            ? 'Please wait for the upload to complete.'
            : 'Manually upload your assets and information directly to escrow as compressed files.'}
        </DialogDescription>
      </DialogHeader>
      {!isUploading && <Separator />}
      <SecureUploader
        isUploading={isUploading}
        setIsUploading={setIsUploading}
        resetModal={handleReset}
        back={getBack}
        contentType={contentType!}
        uploadFiles={uploadFiles}
        cancelUpload={cancelUpload}
        uploadProgress={uploadProgress}
        onUploadComplete={handleUploadComplete}
        onFileRejected={toast.error}
      />
    </Fragment>
  );
};

export default SecureUploadContainer;
